import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchProductAllocation,
  reserveTokensForItem,
  releaseTokensForItem
} from '../features/orders/ordersApi';

/**
 * Where a product's cable can go, and the two things you can do about it.
 *
 * Three screens ask this now — the production planner's table, the sales
 * reserve modal, and the launch wizard — and the writing side is fiddly enough
 * that three copies would drift: reservations go one call per (order, line,
 * item), in the order the preview packed them, because the server re-derives
 * what is free after each write and a different order gives a different answer.
 *
 * So it lives once, here.
 */

const fmt = n => Math.round(Number(n) || 0).toLocaleString();

export default function useProductAllocation({ jobIds = null } = {}) {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);
  const [error, setError] = useState(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['product-allocation', jobIds],
    queryFn: () => fetchProductAllocation(jobIds)
  });

  const products = useMemo(() => data?.products || [], [data]);

  const byJob = useMemo(() => {
    const m = new Map();
    for (const p of products) m.set(String(p.jobId), p);
    return m;
  }, [products]);

  /**
   * By prefix: the same answer is asked for in several shapes — one product,
   * a batch, one order — and refreshing only the shape in front of you leaves
   * the others showing what was true before the write.
   */
  const refresh = () => {
    queryClient.invalidateQueries({
      predicate: q =>
        ['product-allocation', 'token-feasibility'].some(k =>
          String(q.queryKey?.[0] || '').startsWith(k)
        )
    });
    queryClient.invalidateQueries({ queryKey: ['orders'] });
    queryClient.invalidateQueries({ queryKey: ['jobs'] });
  };

  /** Commit a split: one call per (order, line, item), in the packed order. */
  const allocate = async picks => {
    setBusy(true);
    setError(null);
    setNote(null);

    const done = [];
    try {
      for (const pick of picks) {
        const res = await reserveTokensForItem(
          pick.orderId,
          pick.lineId,
          pick.itemId,
          pick.candidates.map(c => ({
            jobId: c.jobId,
            tokenId: c.tokenId,
            units: c.unitsFromThisPiece
          }))
        );
        const got = (res.applied || []).reduce((s, a) => s + a.units, 0);
        if (got) done.push(`${got} × ${fmt(pick.lengthPerUnit)} m`);
        if (res.refused?.length) {
          setError(`Partly refused: ${res.refused.map(r => r.reason).join('; ')}`);
          break;
        }
      }
      if (done.length) setNote(`Sent ${done.join(', ')}.`);
      refresh();
      return true;
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Could not allocate');
      refresh();
      return false;
    } finally {
      setBusy(false);
    }
  };

  /** Give a promise back: the order loses the units, the cable is free again. */
  const release = async (product, promise) => {
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      const res = await releaseTokensForItem(
        promise.orderId,
        promise.lineId,
        promise.itemId,
        product.jobId,
        // No token id: a promise made in the launch wizard never marked a drum,
        // and one made here is found by the job's own reservation anyway.
        null
      );
      setNote(`${fmt(res.meters)} m taken back from ${promise.orderCode}.`);
      refresh();
      return true;
    } catch (err) {
      setError(
        err?.response?.data?.error || err.message || 'Could not take it back'
      );
      refresh();
      return false;
    } finally {
      setBusy(false);
    }
  };

  return {
    products,
    byJob,
    isLoading,
    isError,
    busy,
    note,
    error,
    setNote,
    setError,
    allocate,
    release,
    refresh
  };
}
